'use client'

import { useState, useMemo, useEffect, useRef } from 'react'
import Link from 'next/link'
import { Media } from '@/components/Media'
import { Reveal } from '@/components/Reveal'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type { TeamMember } from '@/payload-types'

const DEPARTMENT_LABELS: Record<string, string> = {
  sales: 'Sales',
  commercial: 'Commercial',
  'property-management': 'Property Management',
  admin: 'Administration & Support',
}

const ALL = 'all'

export function PeopleGridClient({ members }: { members: TeamMember[] }) {
  const gridRef = useRef<HTMLDivElement>(null)
  // Portraits reveal as one batch (Steve 2026-08-20). Waits for the images the
  // browser actually starts loading, with a safety timer so a slow/failed photo
  // can never hold the grid back; the loading-ground behind each box means any
  // straggler still shows the branded placeholder rather than a blank square.
  const [photosReady, setPhotosReady] = useState(false)

  useEffect(() => {
    const root = gridRef.current
    if (!root) return
    // Hydration landed after the page finished loading (slow connection, late
    // JS) — everything is already there, so reveal at once rather than making
    // the visitor wait on a batch that has long since completed.
    if (document.readyState === 'complete') {
      setPhotosReady(true)
      return
    }
    const imgs = Array.from(root.querySelectorAll('img'))
    const pending = imgs.filter((img) => !(img.complete && img.naturalWidth > 0))
    if (pending.length === 0) {
      setPhotosReady(true)
      return
    }
    let remaining = pending.length
    const done = () => {
      remaining -= 1
      if (remaining <= 0) setPhotosReady(true)
    }
    pending.forEach((img) => {
      img.addEventListener('load', done, { once: true })
      img.addEventListener('error', done, { once: true })
    })
    const onWindowLoad = () => setPhotosReady(true)
    window.addEventListener('load', onWindowLoad, { once: true })
    const safety = window.setTimeout(() => setPhotosReady(true), 4000)
    return () => {
      window.clearTimeout(safety)
      window.removeEventListener('load', onWindowLoad)
      pending.forEach((img) => {
        img.removeEventListener('load', done)
        img.removeEventListener('error', done)
      })
    }
    // Mount-only: later filter changes reuse already-cached photos.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [department, setDepartment] = useState<string>(ALL)

  // Build the list of departments that actually have members (skip empty options)
  const availableDepartments = useMemo(() => {
    const set = new Set<string>()
    for (const m of members) {
      const dept = (m as any).department
      if (dept) set.add(dept)
    }
    // Preserve canonical order
    return ['sales', 'commercial', 'property-management', 'admin'].filter((d) => set.has(d))
  }, [members])

  const filtered = useMemo(() => {
    if (department === ALL) return members
    return members.filter((m) => (m as any).department === department)
  }, [members, department])

  return (
    <>
      {/* Filter bar — the trigger keeps the canonical Select styling (Input 2198:40022:
          bg-muted, no border, radius sm, text-body-sm 14/16). Local to this call site:
          the Team-page Trigger instances (4089:188665 / 4089:188670 / 4089:188675) are
          160 wide at every breakpoint and their label is theme/muted-foreground #5a595d.
          The 28px height is canonical and already in ui/select.tsx.
          Trigger bottom → grid top is 32 at 390 and 64 from 768. */}
      <div className="flex items-center justify-start mb-8 md:mb-16">
        <Select value={department} onValueChange={setDepartment}>
          <SelectTrigger
            className="w-auto min-w-40 whitespace-nowrap text-muted-foreground"
            aria-label="Filter team by department"
          >
            <SelectValue placeholder="All Departments" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>All Departments</SelectItem>
            {availableDepartments.map((d) => (
              <SelectItem key={d} value={d}>
                {DEPARTMENT_LABELS[d]}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {/* Grid — Content Card Rows: 1 column mobile (2237:22102) · 2 tablet
          (4022:103196) · 4 desktop (2237:22085). There is no 3-column state.
          Column gap: tablet 4089:184457 card 336 / x-step 368 → 32; desktop 4089:184415
          card 320 / x-step 384 → 64. Row gap: mobile 4089:184501 y-step 215 − 167 → 48;
          tablet + desktop → 64. */}
      {/* Without JS the reveal never runs — keep the portraits visible. */}
      <noscript>
        <style>{`.team-photo-fade { opacity: 1 !important; }`}</style>
      </noscript>
      <div
        ref={gridRef}
        className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-x-8 lg:gap-x-16 gap-y-12 md:gap-y-16"
      >
        {filtered.length === 0 ? (
          <p className="col-span-full text-body-md text-muted-foreground">
            No team members match the selected filter.
          </p>
        ) : (
          filtered.map((member, index) => {
            const phone = member.mobile || member.phone
            return (
              // Staff Card — mobile 4089:199725 (358x167) is HORIZONTAL: two
              // flex-[1_0_0] columns, square photo left. Tablet 4089:199241 (336x460)
              // and desktop 4089:198017 (320x448) stack vertically. gap/gap-6 24px.
              // Steve 2026-08-24 — per-card slideup reveal, staggered by row position
              // (4-column grid at desktop), same treatment as the property cards.
              <Reveal key={member.id} variant="slideup" delay={(index % 4) * 100}>
                <div className="group flex items-start gap-6 md:flex-col">
                  {member.photo && typeof member.photo === 'object' ? (
                    <Link
                      href={member.slug ? `/team/${member.slug}` : '#'}
                      className="block w-full min-w-0 flex-1 md:flex-none"
                    >
                      {/* Steve 2026-08-20: same branded loading ground as the property
                        cards sits behind every portrait; the photos fade in TOGETHER
                        once the batch has loaded, so the grid never pops in one by
                        one. The member's text is unaffected and shows immediately. */}
                      <div className="loading-ground aspect-square overflow-hidden">
                        <div
                          className={`team-photo-fade h-full w-full transition-opacity duration-500 ${
                            photosReady ? 'opacity-100' : 'opacity-0'
                          }`}
                        >
                          <Media
                            resource={member.photo}
                            imgClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                            className="w-full h-full"
                          />
                        </div>
                      </div>
                    </Link>
                  ) : (
                    <Link
                      href={member.slug ? `/team/${member.slug}` : '#'}
                      className="block w-full min-w-0 flex-1 md:flex-none"
                    >
                      {/* Missing-photo placeholder: theme/muted, not the cyan secondary */}
                      <div className="aspect-square overflow-hidden bg-muted" />
                    </Link>
                  )}

                  {/* Text column — gap/gap-2 8px between the name+role group and the
                    phone+button group (I4089:198017;4089:198072). */}
                  <div className="flex w-full min-w-0 flex-1 flex-col items-start gap-2 md:flex-none">
                    <div className="flex w-full flex-col gap-0">
                      {/* Body/bodyLgAlt — font/size/md 18, weight 400 */}
                      <h3 className="font-base text-body font-normal text-foreground">
                        {member.name}
                      </h3>
                      {/* Body/bodyMd — font/size/base 16, weight 300 */}
                      <p className="font-base text-body-md font-light text-foreground">
                        {member.role}
                      </p>
                    </div>

                    <div className="flex w-full flex-col items-start gap-2">
                      {phone && (
                        <a
                          href={`tel:${phone.replace(/\s/g, '')}`}
                          className="font-base text-body-sm font-light text-foreground hover:opacity-60 transition-opacity"
                        >
                          {phone}
                        </a>
                      )}

                      {/* Button size=Mini, type=outline (I4089:198017;4089:198083 /
                        I4089:199725;4022:89040). Figma defines no email link here. */}
                      {member.slug && (
                        <Button asChild variant="outline" size="mini">
                          <Link href={`/team/${member.slug}`}>View Profile</Link>
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              </Reveal>
            )
          })
        )}
      </div>
    </>
  )
}
